import {
  Box,
  FormControl,
  FormHelperText,
  InputAdornment,
  makeStyles,
  MenuItem,
  Select,
  SelectProps,
} from '@material-ui/core';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { Font } from '@/components/shared/components/Font';
import { IconHeader } from '@/components/shared/components/IconHeader';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface SelectCardProps extends SelectProps {
  name: string;
  label: string;
  selectOptions: {
    value: string | number;
    display: string | React.ReactElement;
  }[];
  iconName?: string;
  style?: React.CSSProperties;
}

export const SelectCard = ({
  name,
  label,
  selectOptions,
  iconName = 'tune',
  style,
  ...selectProps
}: SelectCardProps) => {
  const { typography, palette, customBorderRadius } = useCustomTheme();

  const styleDefinition = {
    root: {
      margin: 0,
      '& .MuiInputBase-input': {
        paddingBlock: '12px',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
      },
      '& .MuiSelect-root': { display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end' },
      '& .MuiInputBase-root': {
        alignItems: 'center',
        backgroundColor: palette.grey[50],
        padding: '14px 16px',
        borderRadius: customBorderRadius.paper,
      },
      '& .MuiSelect-icon': {
        marginRight: '16px',
      },
      '& .MuiInputAdornment-root': {
        height: '100%',
        maxHeight: '100%',
      },
      '& .MuiSelect-select.MuiSelect-select': {
        paddingRight: '36px',
      },
      '& .MuiFormHelperText-root': {
        marginTop: '8px',
        marginLeft: '4px',
      },
    },
  };
  const useStyles = makeStyles(styleDefinition);
  const classes = useStyles();

  const control = useFormContext()?.control;

  return (
    <FormControl fullWidth className={classes.root}>
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => {
          return (
            <>
              <Select
                disableUnderline
                onChange={(e) => field.onChange(e.target.value)}
                value={field.value}
                error={!!error?.[name]}
                {...selectProps}
                style={{
                  fontSize: typography.subtitle2.fontSize,
                  color: palette.text.primary,
                  fontWeight: 700,
                  textAlign: 'end',
                  ...style,
                }}
                startAdornment={
                  <InputAdornment position={'start'}>
                    <Box display={'flex'} alignItems={'center'}>
                      <IconHeader iconName={iconName} color={'minor'} />
                      <Box display={'flex'} flexDirection={'column'} alignItems={'flex-start'}>
                        <Font variant={'overline'} color={'textSecondary'} align={'left'}>
                          <Text tId={label} />
                        </Font>
                      </Box>
                    </Box>
                  </InputAdornment>
                }
              >
                {selectOptions.map((option, i) => (
                  <MenuItem key={i} value={option.value} className={classes.root}>
                    {option.display}
                  </MenuItem>
                ))}
              </Select>
              {error && <FormHelperText style={{ color: palette.error.main }}>{error?.[name]?.message}</FormHelperText>}
            </>
          );
        }}
      />
    </FormControl>
  );
};
